import React, { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import JobShareButton from "../components/JobShareButton"
import { getAPIBaseURL } from "../services/helpers"
import { deleteData, getData, postData } from "../services/request"
import {
  applicationStatusLabel,
  employmentTypeLabel,
  jobStatusLabel,
  locationTypeLabel
} from "../utils/employmeLabels"
import { billingSummaryText } from "../utils/billingDisplay"

const EDITABLE_STATUSES = ["DRAFT", "PENDING_PAYMENT", "PENDING_APPROVAL", "REJECTED", "LIVE"]
const DELETABLE_STATUSES = ["DRAFT", "PENDING_PAYMENT", "REJECTED"]
const CLOSABLE_STATUSES = ["LIVE", "PENDING_APPROVAL"]

const canEditJob = (status) => EDITABLE_STATUSES.includes(status)
const canDeleteJob = (status) => DELETABLE_STATUSES.includes(status) || CLOSABLE_STATUSES.includes(status)
const getDeleteLabel = (status) =>
  CLOSABLE_STATUSES.includes(status) ? "Close job" : "Delete"

const EmployerJobDetails = () => {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [genMsg, setGenMsg] = useState("")
  const [paying, setPaying] = useState(false)
  const [confirmingPayment, setConfirmingPayment] = useState(false)
  const [awaitingPaymentConfirm, setAwaitingPaymentConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [paymentRequired, setPaymentRequired] = useState(true)
  const [billing, setBilling] = useState({
    currency: "NGN",
    amount: 10000,
    usedUsdFallback: false,
    countryCurrency: null,
  })
   const [applicants, setApplicants] = useState([])
  const [applicantsLoading, setApplicantsLoading] = useState(true)
  const [applicantsError, setApplicantsError] = useState("")

  const [aiRanking, setAiRanking] = useState(null)
  const [aiRankingLoading, setAiRankingLoading] = useState(false)
  const [aiRankingError, setAiRankingError] = useState("")

  const [commuteSummary, setCommuteSummary] = useState(null)
  const [commuteSummaryLoading, setCommuteSummaryLoading] = useState(false)
  const [commuteSummaryError, setCommuteSummaryError] = useState("")

  const load = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        const j = res.result.data?.data || null
        setJob(j)
        const lt = j?.locationType || j?.remoteType
        if (j && (lt === "ONSITE" || lt === "HYBRID")) {
          loadCommuteSummary()
        } else {
          setCommuteSummary(null)
          setCommuteSummaryError("")
        }
      } else {
        setJob(null)
        setError(res.result.data?.message || "Could not load job")
      }
    } else {
      setJob(null)
      setError(res.result?.data?.message || "Network error")
    }
  }

  const loadApplicants = async () => {
    setApplicantsLoading(true)
    setApplicantsError("")
    const url = `${getAPIBaseURL()}/employme/employer/applicants`
    const headers = {}
    const params = { jobId }
    const res = await getData(url, headers, params)
    setApplicantsLoading(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        setApplicants(res.result.data?.data || [])
      } else {
        setApplicantsError(res.result.data?.message || "Could not load applicants for this job")
      }
    } else {
      setApplicantsError(res.result?.data?.message || "Network error")
    }
  }

  const loadCommuteSummary = async () => {
    if (!jobId) return
    setCommuteSummaryLoading(true)
    setCommuteSummaryError("")
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}/commute-summary`
    const res = await getData(url, {}, {})
    setCommuteSummaryLoading(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        setCommuteSummary(res.result.data?.data || null)
      } else {
        setCommuteSummaryError(res.result.data?.data || res.result.data?.message || "Could not load commute summary")
      }
    } else {
      setCommuteSummaryError(res.result?.data?.data || res.result?.data?.message || "Network error")
    }
  }

  const loadPostingConfig = async () => {
    const url = `${getAPIBaseURL()}/employme/employer/job-posting/config`
    const res = await getData(url, {}, {})
    if (res.error === '' && res.result?.status === 200) {
      const data = res.result.data?.data || res.result.data
      setPaymentRequired(data?.paymentRequired !== false)
      if (data?.billingCurrency || data?.billingAmount != null) {
        setBilling({
          currency: data.billingCurrency || "NGN",
          amount: data.billingAmount != null ? data.billingAmount : 10000,
          usedUsdFallback: Boolean(data.usedUsdFallback),
          countryCurrency: data.countryCurrency || null,
        })
      }
    }
  }

  useEffect(() => {
    loadPostingConfig()
    load()
    loadApplicants()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  const handleAiRanking = async () => {
    setAiRankingError("")
    setAiRankingLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}/ai-ranking`
      const axiosModule = await import("axios")
      const axios = axiosModule.default || axiosModule
      const res = await axios.post(
        url,
        { sortBy: "interview" },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true
        }
      )

      if (res.status === 200) {
        setAiRanking(res.data?.data || null)
      } else {
        setAiRankingError(res.data?.data || res.data?.message || "Could not rank candidates")
      }
    } catch (e) {
      setAiRankingError("Network error while ranking candidates")
    } finally {
      setAiRankingLoading(false)
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const formatSalaryRange = (range) => {
    if (!range) return null
    const min = range.min
    const max = range.max
    if (min == null && max == null) return null
    if (typeof min === "number" && typeof max === "number" && min === 0 && max === 0) {
      return null
    }
    const fmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })
    const cur = range.currency || "NGN"
    if (min != null && max != null) {
      return `${fmt.format(min)} – ${fmt.format(max)} ${cur}`
    }
    if (min != null) return `From ${fmt.format(min)} ${cur}`
    if (max != null) return `Up to ${fmt.format(max)} ${cur}`
    return null
  }

  const handleDeleteJob = async () => {
    if (!job) return
    const label = getDeleteLabel(job.status)
    const message =
      job.status === "LIVE" || job.status === "PENDING_APPROVAL"
        ? "Close this job? It will no longer accept new applicants."
        : "Delete this job permanently? This cannot be undone."
    if (!window.confirm(message)) return

    setDeleting(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}`
    const res = await deleteData(url, {}, {})
    setDeleting(false)
    if (res.error === "" && res.result?.status === 200) {
      const closed = res.result.data?.data?.closed
      if (closed) {
        setGenMsg("Job closed successfully.")
        await load()
      } else {
        navigate("/employer/jobs")
      }
    } else {
      setError(res.result?.data?.data || res.result?.data?.message || `Could not ${label.toLowerCase()} job`)
    }
  }

  const handleProceedToPayment = async () => {
    setGenMsg("")
    setError("")
    setPaying(true)
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}/initiate-payment`
    const headers = { "Content-Type": "application/x-www-form-urlencoded" }
    const params = new URLSearchParams()
    const res = await postData(url, headers, params.toString())
    setPaying(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        const payload = res.result.data?.data || {}
        const redirectUrl = payload.paymentUrl || payload.redirectUrl
        if (redirectUrl) {
          if (payload.hasReturnUrls) {
            window.location.href = redirectUrl
            return
          }
          window.open(redirectUrl, "_blank", "noopener,noreferrer")
          setAwaitingPaymentConfirm(true)
          setGenMsg("Complete payment in the new tab, then confirm below. Local/dev does not use Bachs redirects or webhooks.")
          return
        }
        if (payload.paymentRequired === false || payload.submitted) {
          setGenMsg("Job submitted for review. It will go live automatically if approved by AI.")
          await load()
          return
        }
        setGenMsg(res.result.data?.message || "Payment initialized.")
      } else {
        setError(res.result.data?.message || "Could not start payment")
      }
    } else {
      setError(res.result?.data?.message || "Network error")
    }
  }

  const handleConfirmPayment = async () => {
    setError("")
    setGenMsg("")
    setConfirmingPayment(true)
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}/verify-payment`
    const headers = { "Content-Type": "application/x-www-form-urlencoded" }
    const res = await postData(url, headers, "")
    setConfirmingPayment(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        setAwaitingPaymentConfirm(false)
        setGenMsg("Payment confirmed. Your job is being reviewed.")
        await load()
      } else {
        setError(res.result.data?.data || res.result.data?.message || "Payment is not completed yet")
      }
    } else {
      setError(res.result?.data?.data || res.result?.data?.message || "Could not confirm payment")
    }
  }

  const applicantsForJob = applicants || []
  const skills = Array.isArray(job?.skills)
    ? job.skills
    : typeof job?.skills === "string"
    ? job.skills.split(",").map((s) => s.trim()).filter(Boolean)
    : []
  const eligibilityQuestions = job?.screening?.eligibilityQuestions || []
  const requiredDocuments = job?.screening?.requiredDocuments || []

  return (
    <div className="mx-auto max-w-5xl px-3 sm:px-0">
      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-2xl">
            Job details
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Review job configuration, manage payment, and view applicants.
          </p>
          {job ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <p className="text-xs text-gray-500">
                Status:{" "}
                <span className="font-semibold">
                  {jobStatusLabel(job.status)}
                </span>
              </p>
              {canEditJob(job.status) ? (
                <Link
                  to={`/employer/jobs/${jobId}/edit`}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Edit job
                </Link>
              ) : null}
              {canDeleteJob(job.status) ? (
                <button
                  type="button"
                  onClick={handleDeleteJob}
                  disabled={deleting}
                  className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deleting ? "..." : getDeleteLabel(job.status)}
                </button>
              ) : null}
              {job.status === "LIVE" ? (
                <>
                  <Link
                    to={`/employer/cv-screening/new?jobId=${jobId}`}
                    className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-900"
                  >
                    Screen CVs
                  </Link>
                  <JobShareButton
                    jobId={jobId}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : !job ? (
            <div className="text-sm text-gray-500">{error || "Job not found"}</div>
          ) : (
            <>
              {error ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
                  {error}
                </div>
              ) : null}
              {genMsg ? (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">
                  {genMsg}
                </div>
              ) : null}

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {job.title || "Untitled role"}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {(
                    typeof job.location === "string"
                      ? job.location
                      : [job.location?.city, job.location?.state, job.location?.country]
                          .filter(Boolean)
                          .join(", ")
                  ) || "Location not set"}{" "}
                  •{" "}
                  {employmentTypeLabel(job.employmentType || job.type)}{" "}
                  •{" "}
                  {locationTypeLabel(job.locationType || job.remoteType)}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Created: {formatDate(job.createdAt)}
                </p>
                {(job.jobStartDate || job.jobEndDate) ? (
                  <p className="mt-1 text-xs text-gray-500">
                    Timeline: {job.jobStartDate ? formatDate(job.jobStartDate) : "—"} to {job.jobEndDate ? formatDate(job.jobEndDate) : "—"}
                  </p>
                ) : null}
                {formatSalaryRange(job.salaryRange) ? (
                  <p className="mt-2 text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">Salary: </span>
                    {formatSalaryRange(job.salaryRange)}
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">Salary: not set</p>
                )}
              </div>

              {(job.status === "DRAFT" || job.status === "PENDING_PAYMENT") && (
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {paymentRequired
                          ? "Complete payment to publish this job"
                          : "Submit this job for review"}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        {paymentRequired
                          ? `${billingSummaryText(billing)} You can still edit the job details before it goes live.`
                          : "No payment is required. Your job will be reviewed by AI and listed if approved."}
                      </p>
                    </div>
                    <div className="flex w-full flex-col gap-2 sm:w-auto">
                      <button
                        type="button"
                        onClick={handleProceedToPayment}
                        disabled={paying || confirmingPayment}
                        className="w-full rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400 sm:w-auto"
                      >
                        {paying
                          ? paymentRequired
                            ? "Starting payment..."
                            : "Submitting..."
                          : paymentRequired
                          ? awaitingPaymentConfirm
                            ? "Open payment again"
                            : "Proceed to payment"
                          : "Submit for review"}
                      </button>
                      {awaitingPaymentConfirm ? (
                        <button
                          type="button"
                          onClick={handleConfirmPayment}
                          disabled={confirmingPayment}
                          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                        >
                          {confirmingPayment ? "Confirming..." : "I've completed payment"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {(job.locationType === "HYBRID" ||
                job.locationType === "ONSITE" ||
                job.remoteType === "HYBRID" ||
                job.remoteType === "ONSITE") && (
                <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Commute &amp; transport analysis (applicants)
                  </h4>
                  <p className="mt-1 text-xs text-gray-500">
                    Estimates use map pins on the job and each candidate profile. Averages include only
                    applicants with both pins.
                  </p>
                  {commuteSummaryLoading ? (
                    <p className="mt-3 text-sm text-gray-500">Loading analysis…</p>
                  ) : commuteSummaryError ? (
                    <p className="mt-3 text-sm text-red-600">{commuteSummaryError}</p>
                  ) : commuteSummary?.applicable === false ? (
                    <p className="mt-3 text-sm text-gray-700">{commuteSummary.summaryText}</p>
                  ) : commuteSummary ? (
                    <div className="mt-3 space-y-3 text-sm text-gray-700">
                      <p className="text-xs text-gray-600 whitespace-pre-line">
                        {commuteSummary.summaryText}
                      </p>
                      <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded-md bg-gray-50 px-3 py-2">
                          <dt className="text-[10px] font-semibold uppercase text-gray-500">Applicants</dt>
                          <dd className="font-semibold text-gray-900">{commuteSummary.applicantCount}</dd>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2">
                          <dt className="text-[10px] font-semibold uppercase text-gray-500">With distance</dt>
                          <dd className="font-semibold text-gray-900">
                            {commuteSummary.withDistanceEstimateCount}
                          </dd>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2">
                          <dt className="text-[10px] font-semibold uppercase text-gray-500">Avg km (one-way)</dt>
                          <dd className="font-semibold text-gray-900">
                            {commuteSummary.averageDistanceKm != null
                              ? `${commuteSummary.averageDistanceKm} km`
                              : "—"}
                          </dd>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2">
                          <dt className="text-[10px] font-semibold uppercase text-gray-500">Avg transport / mo</dt>
                          <dd className="font-semibold text-gray-900">
                            {commuteSummary.averageMonthlyTransport != null &&
                            commuteSummary.currency &&
                            ["NGN", "USD", "EUR"].includes(commuteSummary.currency)
                              ? (() => {
                                  try {
                                    return new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: commuteSummary.currency,
                                      maximumFractionDigits: 0
                                    }).format(commuteSummary.averageMonthlyTransport)
                                  } catch {
                                    return `${commuteSummary.averageMonthlyTransport} ${commuteSummary.currency}`
                                  }
                                })()
                              : commuteSummary.averageMonthlyTransportNgN != null
                                ? `${Number(commuteSummary.averageMonthlyTransportNgN).toLocaleString()} ${commuteSummary.currency || "NGN"}`
                                : "—"}
                          </dd>
                        </div>
                      </dl>
                      {!commuteSummary.jobHasWorksitePin && (
                        <p className="text-xs font-medium text-amber-800">
                          Add a precise job location pin when editing the post so distances can be calculated.
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              <div className="mb-6 grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Description
                  </h4>
                  <p className="mt-2 whitespace-pre-line text-sm text-gray-700">
                    {job.description || "No description provided."}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Interview configuration
                  </h4>
                  <p className="mt-2 text-xs text-gray-600">
                    {(job.interviewQuestions || []).length} question(s) •{" "}
                    {job.interviewTimeLimitSeconds || 60}s per question • max{" "}
                    {job.interviewMaxTakes || 1} take(s)
                  </p>
                  {(job.interviewQuestions || []).length ? (
                    <ul className="mt-3 list-disc pl-5 text-sm text-gray-700 space-y-1">
                      {(job.interviewQuestions || []).map((q, idx) => (
                        <li key={`${idx}-${q?.slice?.(0, 12) || "q"}`}>
                          {q}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-sm text-gray-500">
                      No interview questions saved yet.
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-6 grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Requirements
                  </h4>
                  <p className="mt-2 whitespace-pre-line text-sm text-gray-700">
                    {job.requirements || "No requirements provided."}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Skills</h4>
                  {skills.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {skills.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-800"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">
                      No skills provided.
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-6 grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Eligibility questions
                  </h4>
                  {eligibilityQuestions.length ? (
                    <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                      {eligibilityQuestions.map((q, idx) => (
                        <li key={q._id || `${idx}-${q.question || "q"}`}>
                          {q.question}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">
                      No eligibility questions.
                    </p>
                  )}
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Required documents
                  </h4>
                  {requiredDocuments.length ? (
                    <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                      {requiredDocuments.map((d, idx) => (
                        <li key={d._id || `${idx}-${d.label || "doc"}`}>
                          {d.label}
                          {d.isMandatory !== false ? (
                            <span className="ml-2 text-xs font-medium text-gray-500">
                              (required)
                            </span>
                          ) : (
                            <span className="ml-2 text-xs font-medium text-gray-400">
                              (optional)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">
                      No required documents.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 px-4 py-3 sm:px-5 sm:py-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Applicants
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    {applicantsLoading
                      ? "Loading applicants for this job..."
                      : applicantsForJob.length
                      ? "Review candidates, watch videos, and score applications."
                      : "You don’t have any applicants yet."}
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {job.status === "LIVE" ? (
                      <Link
                        to={`/employer/cv-screening/new?jobId=${jobId}`}
                        className="rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-2 text-xs font-semibold text-cyan-900 hover:bg-cyan-100"
                      >
                        Screen CVs (bulk upload)
                      </Link>
                    ) : null}
                    <Link
                      to={`/employer/jobs/${jobId}/ranking`}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      Open ranking page
                    </Link>
                    <button
                      type="button"
                      onClick={handleAiRanking}
                      disabled={aiRankingLoading || applicantsForJob.length === 0 || applicantsLoading}
                      className="rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                      {aiRankingLoading ? "Ranking..." : "AI rank candidates"}
                    </button>
                    {aiRankingError ? (
                      <p className="text-xs text-red-600">{aiRankingError}</p>
                    ) : null}
                  </div>

                  {aiRanking?.champion?.applicationId ? (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-900">Top Candidate (Champion)</p>
                      <p className="mt-1 text-[11px] text-gray-700 whitespace-pre-line">
                        {aiRanking.champion.explanation || "Champion selected."}
                      </p>
                    </div>
                  ) : null}
                </div>
                {aiRanking?.ranking?.length ? (
                  <div className="mt-4 px-4 sm:px-0">
                    <h4 className="text-xs font-semibold text-gray-900">AI Ranking</h4>
                    <ul className="mt-2 space-y-1">
                      {aiRanking.ranking.slice(0, 10).map((r) => {
                        const appId = r.applicationId
                        const match = applicantsForJob.find(a => String(a._id) === String(appId)) || {}
                        const candidateName =
                          r.candidateName ||
                          match.candidate?.name ||
                          match.name ||
                          match.candidateName ||
                          match.candidate?.fullName ||
                          match.fullName ||
                          "Candidate"
                        return (
                          <li key={appId} className="text-xs text-gray-700 flex flex-wrap items-center gap-2">
                            <span className="inline-flex min-w-5 items-center justify-center rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700">
                              #{r.position ?? "?"}
                            </span>
                            <Link
                              to={`/employer/jobs/${jobId}/applicants/${appId}`}
                              className="font-medium text-gray-900 hover:underline"
                            >
                              {candidateName}
                            </Link>
                            <span className="text-gray-500">Score: {r.score ?? "N/A"}</span>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ) : null}

                {applicantsLoading ? (
                  <div className="p-4 text-sm text-gray-500">Loading applicants...</div>
                ) : applicantsError ? (
                  <div className="p-4 text-sm text-red-600">{applicantsError}</div>
                ) : applicantsForJob.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No applicants yet.</div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {applicantsForJob.map((a) => {
                      const candidate = a.candidate || {}
                      const candidateName =
                        candidate.name || a.name || a.candidateName || "Candidate"
                      const candidateEmail =
                        candidate.email || a.email || null
                      const scoreValue =
                        typeof a.totalScore === "number"
                          ? a.totalScore
                          : typeof a.score === "number"
                          ? a.score
                          : null

                      return (
                      <li key={a._id} className="p-4 sm:p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900">
                              {candidateName}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              {candidateEmail || "Email not provided"}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              Applied: {formatDate(a.createdAt)}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              Status:{" "}
                              <span className="font-medium">
                                {applicationStatusLabel(a.status)}
                              </span>
                              {typeof a.matchingScore === "number" ? <> • Matching: {a.matchingScore}</> : null}
                              {typeof a.interviewScore === "number" ? <> • Interview: {a.interviewScore}</> : null}
                              {scoreValue != null ? <> • Total: {scoreValue}</> : null}
                            </p>
                          </div>
                          <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-nowrap sm:shrink-0">
                            <Link
                              to={`/employer/jobs/${jobId}/applicants/${a._id}`}
                              className="w-full sm:w-auto rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 text-center"
                            >
                              View & score
                            </Link>
                          </div>
                        </div>
                      </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmployerJobDetails

